import { createSlice, isAnyOf, isFulfilled, isPending, isRejected, nanoid } from '@reduxjs/toolkit';
import {
  DEFAULT_TOAST_TEST_ID,
  MAX_TOASTS,
  STICKY_TONES,
} from './feedbackConstants';

/**
 * What the platform is telling the user right now, wherever they are.
 *
 * The toasts used to be a `Notice` that each page mounted with its own state,
 * below the modals and gone after 6 s. Here they are one stack above everything,
 * fed by any action — which is what lets a job that ends on another page, or an
 * error raised while a dialog is open, still reach the user.
 *
 * Not persisted: a message is about the moment it was said.
 */
const initialState = {
  toasts: [],
  /** Progress of work running in the browser, by the thunk's `requestId`. */
  progress: {},
  /**
   * Job kinds whose outcome some mounted screen is already showing in full —
   * the upload panel, for instance. A toast on top of it would say the same
   * thing twice.
   */
  focusedKinds: {},
  /**
   * Every thunk on its way, by `requestId`: `{type, key}`. The activity bar
   * reads it to say that something is happening, and a trigger to say that
   * its own request is — so neither keeps a flag of its own. A call made in
   * the background is not here: nobody is waiting on it.
   */
  inFlight: {},
};

/**
 * Whether a thunk was called in the background (`arg.background`), as the
 * operator's lists are asked again while the screen sits still: it lights
 * nothing, and its screen shows no loading.
 */
export const isBackgroundRequest = (action) => action.meta?.arg?.background === true;

const LIFECYCLE_SUFFIX = /\/(pending|fulfilled|rejected)$/;

const typePrefixOf = (thunkOrType) =>
  typeof thunkOrType === 'string' ? thunkOrType : thunkOrType.typePrefix;

const asKey = (value) => (typeof value === 'string' || typeof value === 'number' ? value : undefined);

/**
 * What tells one call of a thunk from another: the row it acts on. A thunk
 * names it with `getPendingMeta: ({arg}) => ({pendingKey})`; otherwise it is
 * the argument itself when that is an id, or the argument's `id`.
 */
const pendingKeyOf = ({ pendingKey, arg }) => asKey(pendingKey) ?? asKey(arg) ?? asKey(arg?.id) ?? null;

const sameSlot = (toast, incoming) => {
  // A specific handle is one place on screen: a new message for it replaces
  // the old one, the way the page notice it stands for always did. Two
  // elements with the same handle would also break every test that looks it up.
  if (incoming.testId !== DEFAULT_TOAST_TEST_ID) return toast.testId === incoming.testId;
  return toast.testId === DEFAULT_TOAST_TEST_ID && toast.dedupeKey === incoming.dedupeKey;
};

const keyOf = ({ tone, title, message }) => `${tone}|${title ?? ''}|${message}`;

/**
 * Puts a toast on screen. It takes the place of `targetId` when that toast is
 * still there, otherwise of whatever toast holds its slot, otherwise it goes on
 * top of the stack. Any other toast in the same slot goes: a slot is shown once.
 */
const place = (state, toast, targetId = null) => {
  let index = targetId === null ? -1 : state.toasts.findIndex((item) => item.id === targetId);
  if (index === -1) index = state.toasts.findIndex((item) => sameSlot(item, toast));

  if (index === -1) {
    state.toasts.push(toast);
    index = state.toasts.length - 1;
  } else {
    // In place, so the stack does not jump; the new id restarts its timer.
    state.toasts[index] = toast;
  }

  state.toasts = state.toasts.filter((item, position) => position === index || !sameSlot(item, toast));

  while (state.toasts.length > MAX_TOASTS) {
    // Good news already on screen makes room first: it was going away on its
    // own anyway. The toast just placed is never the one dropped.
    const candidates = state.toasts.filter((item) => item.id !== toast.id);
    const victim = candidates.find((item) => !item.sticky) ?? candidates[0];
    state.toasts = state.toasts.filter((item) => item.id !== victim.id);
  }
};

const feedbackSlice = createSlice({
  name: 'feedback',
  initialState,
  reducers: {
    toastShown: {
      reducer: (state, action) => {
        place(state, action.payload);
      },
      prepare: ({
        id,
        tone,
        message,
        title = null,
        testId = DEFAULT_TOAST_TEST_ID,
        action = null,
        sticky,
        progress = null,
        dedupeKey,
      }) => ({
        payload: {
          id: id ?? nanoid(),
          tone,
          message,
          title,
          testId,
          action,
          sticky: sticky ?? STICKY_TONES.includes(tone),
          progress,
          dedupeKey: dedupeKey ?? keyOf({ tone, title, message }),
          createdAt: Date.now(),
        },
      }),
    },

    /**
     * Rewrites a toast that is already on screen, e.g. a progress toast that
     * becomes its outcome. Falls back to showing it when it is gone, so an
     * outcome is never lost because the user closed the progress.
     */
    toastReplaced: (state, action) => {
      const { id, ...next } = action.payload;
      place(
        state,
        {
          ...next,
          id: nanoid(),
          title: next.title ?? null,
          action: next.action ?? null,
          progress: next.progress ?? null,
          testId: next.testId ?? DEFAULT_TOAST_TEST_ID,
          sticky: next.sticky ?? STICKY_TONES.includes(next.tone),
          dedupeKey: next.dedupeKey ?? keyOf(next),
          createdAt: Date.now(),
        },
        id
      );
    },

    toastDismissed: (state, action) => {
      state.toasts = state.toasts.filter((toast) => toast.id !== action.payload);
    },

    progressUpdated: (state, action) => {
      const { key, done, total, label = null } = action.payload;
      state.progress[key] = { done, total, label };

      state.toasts.forEach((toast) => {
        if (toast.id === key) toast.progress = { done, total };
      });
    },

    progressCleared: (state, action) => {
      delete state.progress[action.payload];
    },

    jobFocused: (state, action) => {
      state.focusedKinds[action.payload] = (state.focusedKinds[action.payload] ?? 0) + 1;
    },

    jobUnfocused: (state, action) => {
      const count = (state.focusedKinds[action.payload] ?? 0) - 1;
      if (count > 0) state.focusedKinds[action.payload] = count;
      else delete state.focusedKinds[action.payload];
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher((action) => isPending(action) && !isBackgroundRequest(action), (state, action) => {
        state.inFlight[action.meta.requestId] = {
          type: action.type.replace(LIFECYCLE_SUFFIX, ''),
          key: pendingKeyOf(action.meta),
        };
      })
      .addMatcher(isAnyOf(isFulfilled, isRejected), (state, action) => {
        delete state.inFlight[action.meta.requestId];
        delete state.progress[action.meta.requestId];
      });
  },
});

export const {
  toastShown,
  toastReplaced,
  toastDismissed,
  progressUpdated,
  progressCleared,
  jobFocused,
  jobUnfocused,
} = feedbackSlice.actions;

export const selectToasts = (state) => state.feedback.toasts;

/** `{done, total, label}` of a piece of browser work, or `null`. */
export const selectProgress = (key) => (state) => state.feedback.progress[key] ?? null;

export const selectIsKindFocused = (kind) => (state) =>
  (state.feedback.focusedKinds[kind] ?? 0) > 0;

/**
 * Whether a thunk is running: any call of it, or the one for `key` (the row it
 * acts on, as `pendingKeyOf` reads it). Takes the thunk or its `typePrefix`.
 */
export const selectIsRunning = (thunkOrType, key) => (state) => {
  const type = typePrefixOf(thunkOrType);
  return Object.values(state.feedback?.inFlight ?? {}).some(
    (entry) => entry.type === type && (key === undefined || String(entry.key) === String(key))
  );
};

const requestIdsInFlight = (state) => Object.keys(state.feedback?.inFlight ?? {});

/** Whether the user is waiting on any request — what lights the activity bar. */
export const selectIsBusy = (state) => requestIdsInFlight(state).length > 0;

/**
 * How far along the requests the user is waiting on are, 0-100, when every one
 * of them reports it (a file going up). `null` when any of them cannot say.
 */
export const selectBusyPercentage = (state) => {
  const requestIds = requestIdsInFlight(state);
  const reports = requestIds.map((requestId) => state.feedback.progress[requestId]);
  if (requestIds.length === 0 || reports.some((report) => !report?.total)) return null;

  const done = reports.reduce((sum, report) => sum + report.done, 0);
  const total = reports.reduce((sum, report) => sum + report.total, 0);
  return Math.min(100, Math.round((done / total) * 100));
};

export default feedbackSlice.reducer;
